import { Attachment as AttachmentIcon, Image } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { Container } from '@mui/system';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { FaArrowLeft } from 'react-icons/fa';
import { TbFileText } from 'react-icons/tb';
import { useNavigate, useParams } from 'react-router-dom';
import { imgUrl, TOKEN_KEY, url } from 'src/Helpers/utils/Constant';
import Toaster from 'src/Toaster/Toaster';
import { IAnnoucement } from 'src/Types/module/announcement/announcementType';
import PageTitle from 'src/components/PageTitle';
import CommonPdfAndImageVeiw from 'src/components/pdfAndImageView/CommonPdfAndImageVeiw';

const SingleAnnoucement: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [singleAnnouncement, setSingleAnnouncement] =
    useState<IAnnoucement | null>(null);
  const Toast = Toaster();
  const { slug } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const token = localStorage.getItem(TOKEN_KEY);

  useEffect(() => {
    if (!slug) return;

    const fetchAnnouncement = async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(
          `${url}/api/v1/admin/announcement/${slug}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data',
            },
          },
        );

        setSingleAnnouncement(data.data);
        setLoading(false);
      } catch (err: any) {
        Toast.fire({
          icon: 'error',
          title: `${err.response?.data?.message}`,
        });
        setLoading(false);
      }
    };

    fetchAnnouncement();
  }, [slug]);

  return (
    <>
      <Box sx={{ mx: 4, mt: 0 }}>
        <Card sx={{ padding: 2, marginBottom: 3 }}>
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            width='100%'
          >
            <PageTitle heading={`Announcement Details`} />
            <Button
              variant='outlined'
              color='primary'
              startIcon={<FaArrowLeft />}
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                px: 2,
              }}
            >
              Back to List
            </Button>
          </Box>
        </Card>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
          <LoadingButton loading variant='outlined'>
            Loading...
          </LoadingButton>
        </Box>
      ) : (
        <Container maxWidth='md' sx={{ mb: 5 }}>
          <Card
            sx={{
              borderRadius: '16px',
              boxShadow: '0 8px 30px rgba(0, 0, 0, 0.08)',
              border: '1px solid #e2e8f0',
              overflow: 'hidden',
              bgcolor: 'background.paper',
              padding: 0,
            }}
          >
            {/* Header / Media Section */}
            <Box sx={{ position: 'relative', height: 350, overflow: 'hidden' }}>
              <CardMedia
                component='img'
                image={
                  singleAnnouncement?.thumbnail &&
                  singleAnnouncement.thumbnail !== 'undefined'
                    ? `${imgUrl}/${singleAnnouncement.thumbnail}`
                    : '/static/images/placeholders/important-announcement.jpg'
                }
                alt='Announcement banner'
                sx={{
                  padding: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transition: 'transform 0.5s ease',
                  '&:hover': {
                    transform: 'scale(1.03)',
                  },
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  background:
                    'linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.6))',
                }}
              />

              {singleAnnouncement?.thumbnail && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 20,
                    right: 20,
                    zIndex: 10,
                    borderRadius: '12px',
                    backdropFilter: 'blur(12px)',
                    background: 'rgba(255, 255, 255, 0.15)',
                    boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.25)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.25)',
                      transform: 'scale(1.05) translateY(-2px)',
                      boxShadow: '0 12px 40px 0 rgba(0, 0, 0, 0.35)',
                    },
                    '& button': {
                      background: 'transparent !important',
                      color: '#ffffff !important',
                      textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                      fontWeight: 700,
                      px: 1,
                      p: 1,
                      border: 'none',
                      fontSize: '0.875rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    },
                  }}
                >
                  <CommonPdfAndImageVeiw
                    file={singleAnnouncement.thumbnail}
                    fileName='View Full Image'
                  />
                </Box>
              )}
            </Box>

            <CardContent sx={{ p: 2 }}>
              {/* Type Badges */}

              <Stack direction='row' spacing={1} sx={{ mb: 1.5 }}>
                {(Array.isArray(singleAnnouncement?.type)
                  ? singleAnnouncement.type
                  : [singleAnnouncement?.type]
                ).map((t, idx) => {
                  if (!t) return null;
                  const typeUpper = t.toUpperCase();
                  const color =
                    typeUpper === 'NEWS'
                      ? 'info'
                      : typeUpper === 'NOTICE'
                        ? 'primary'
                        : typeUpper === 'MEMBER_NOTICE'
                          ? 'warning'
                          : 'default';

                  return (
                    <Chip
                      key={idx}
                      label={typeUpper.replace('_', ' ')}
                      color={color as any}
                      size='small'
                      sx={{ fontWeight: 700, borderRadius: '6px' }}
                    />
                  );
                })}
              </Stack>
              {/* Title */}
              <Typography
                variant='overline'
                sx={{
                  color: 'primary.main',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  letterSpacing: '1.2px',
                  display: 'block',
                }}
              >
                Title :
              </Typography>
              <Typography
                variant='h5'
                sx={{
                  fontWeight: 800,
                  mb: 1,
                  color: 'text.primary',
                  lineHeight: 1.3,
                }}
              >
                {singleAnnouncement?.title}
              </Typography>
              <Divider sx={{ mb: 1 }} />
              {/* Main Content */}
              <Typography
                variant='overline'
                sx={{
                  color: 'primary.main',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  letterSpacing: '1.2px',
                  display: 'block',
                }}
              >
                Content :
              </Typography>
              <Typography
                variant='h5'
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.8,
                  // fontSize: "1.05rem",
                  whiteSpace: 'pre-wrap',
                  mb: 1,
                }}
              >
                {singleAnnouncement?.content}
              </Typography>
              <Divider sx={{ mb: 1 }} />
              {/* Attachments Section */}
              {singleAnnouncement?.attachments &&
                singleAnnouncement?.attachments?.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Stack
                      direction='row'
                      alignItems='center'
                      spacing={1}
                      sx={{ mb: 1 }}
                    >
                      <AttachmentIcon color='action' />
                      <Typography
                        variant='h5'
                        sx={{ fontWeight: 700, color: 'text.primary' }}
                      >
                        Attachments & Files
                      </Typography>
                    </Stack>

                    <Grid container spacing={2}>
                      {singleAnnouncement.attachments.map((attachment) => {
                        const isPdf = attachment.url
                          .toLowerCase()
                          .endsWith('.pdf');
                        const fileName = attachment.url.split('/').pop();

                        return (
                          <Grid item xs={12} sm={6} key={attachment.id}>
                            <Paper
                              variant='outlined'
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                p: 2,
                                borderRadius: '12px',
                                border: '1px solid #e2e8f0',
                                bgcolor: '#f8fafc',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                  borderColor: 'primary.main',
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1.5,
                                  minWidth: 0,
                                }}
                              >
                                {isPdf ? (
                                  <TbFileText size={24} color='#3b82f6' />
                                ) : (
                                  <Image color='primary' />
                                )}
                                <Typography
                                  sx={{
                                    fontSize: 14,
                                    fontWeight: 600,
                                    color: 'text.primary',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: { xs: 150, sm: 180 },
                                  }}
                                >
                                  {fileName}
                                </Typography>
                              </Box>

                              <CommonPdfAndImageVeiw
                                file={attachment.url}
                                fileName='View'
                              />
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                )}
            </CardContent>
          </Card>
        </Container>
      )}
    </>
  );
};

export default SingleAnnoucement;
